import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSignedHundredths, parseDioptreToHundredths } from '@optiflow/shared';
import { flipSign } from './signedPower';

interface SignedPowerInputProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** A reading addition is always positive, so it gets no sign control. */
  signed?: boolean;
}

/**
 * A dioptric power field that can be made negative on any keyboard.
 *
 * The iPhone and iPad decimal keypad has no minus key, so the sign gets its own control. The value
 * stays a string throughout — the control edits the text, it never parses it into a number.
 */
export function SignedPowerInput({
  label,
  value,
  onChange,
  placeholder,
  signed = true,
}: SignedPowerInputProps) {
  const { t } = useTranslation();
  const id = useId();

  const normalise = () => {
    const hundredths = parseDioptreToHundredths(value);
    // An unreadable value stays as typed, so the server's message can say what is wrong with it.
    if (hundredths === null || (!signed && hundredths < 0)) return;
    onChange(formatSignedHundredths(hundredths));
  };

  return (
    <div className="w-full">
      <label htmlFor={id} className="field-label">
        {label}
      </label>

      {/* Sign and digits read left to right as one unit in every language, so the control sits where the sign is drawn. */}
      <div
        dir="ltr"
        className="border-line focus-within:border-brand-500 focus-within:ring-brand-100 flex overflow-hidden rounded-lg border bg-white focus-within:ring-4"
      >
        {signed ? (
          <button
            type="button"
            onClick={() => onChange(flipSign(value))}
            aria-label={t('sales.optical.flipSign', { field: label })}
            className="border-line text-ink hover:bg-canvas focus-visible:bg-canvas border-e px-3 text-sm font-semibold focus-visible:outline-none"
          >
            ±
          </button>
        ) : null}
        <input
          id={id}
          dir="ltr"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={normalise}
          className="placeholder:text-muted/70 w-full min-w-0 bg-transparent px-3 py-2.5 text-sm focus:outline-none rtl:text-right"
        />
      </div>
    </div>
  );
}
